import { useEffect, useRef, useState } from 'react';

import { useEnvironment } from 'context/environment';
import { useUploadContext } from 'context/upload';
import { createPresigned } from 'services/api/uploads';

import Upload, { UploadState } from './Upload';

type UseDirectUploadOptions = {
  defaultDocType?: string;
};

export default function useDirectUpload(initialState: Upload[] = [], options?: UseDirectUploadOptions) {
  const { environment } = useEnvironment();
  const { defaultDocType } = options || {};

  const context = useUploadContext();
  const useSharedState = !!context;

  const [localUploads, setLocalUploads] = useState<Upload[]>(initialState);
  const localSequentialId = useRef(Math.max(...initialState.map((x) => +x.id), 0) + 1);

  const uploads = useSharedState ? context!.uploads : localUploads;
  const setUploads = useSharedState ? context!.setUploads : setLocalUploads;
  const getNextId = useSharedState
    ? context!.getNextSequentialId
    : () => {
        return '' + localSequentialId.current++;
      };

  useEffect(() => {
    if (useSharedState && initialState.length > 0 && context!.uploads.length === 0) {
      context!.setUploads(initialState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasProcessingUploads = uploads.some((upload) => upload.isProcessing);

  const [uploading, setUploading] = useState(hasProcessingUploads);

  useEffect(() => {
    setUploading(hasProcessingUploads);
  }, [hasProcessingUploads]);

  const updateFiles = (upload: Upload) => {
    setUploads((uploads) => uploads.map((x) => (x.id != upload.id ? x : new Upload(upload))));
  };

  const uploadFiles = async (files: File[]) => {
    if (uploading) return null;

    const newUploads = files.map(
      (x) =>
        new Upload({
          id: getNextId(),
          file: x,
          uploadDomain: environment?.OLIO_FILES_DOMAIN,
          docType: defaultDocType,
        })
    );

    setUploads((prevUploads) => [...prevUploads, ...newUploads]);

    setUploading(true);

    try {
      const results = await Promise.all(
        newUploads.map((upload) =>
          upload.start(createPresigned, updateFiles).catch((error) => {
            // If presign or upload fails, mark the upload as errored
            upload.status = UploadState.error;
            upload.error = error;
            updateFiles(upload);
          })
        )
      );
      return results;
    } finally {
      setUploading(false);
    }
  };

  const removeUpload = (id: string) => {
    setUploads((prevUploads) => prevUploads.filter((x) => x.id != id));
  };

  const setDocType = (id: string, docType: string | null) => {
    const upload = uploads.find((x) => x.id == id)!;
    upload.docType = docType ?? undefined;
    updateFiles(upload);
  };

  const resetUploads = () => {
    setUploads([]);
  };

  return {
    removeUpload,
    setDocType,
    uploadFiles,
    resetUploads,
    setUploads,
    uploads,
    uploading,
  };
}
